<template>
  <div class="external-assets">
    <header class="section-header">
      <div>
        <h1>Activos externos</h1>
        <p class="muted">Registra posiciones en otras plataformas para verlas en el overview.</p>
      </div>
      <button class="primary" @click="openModal()">
        + Añadir activo
      </button>
    </header>

    <section v-if="loading" class="assets-grid skeleton-assets" role="status" aria-live="polite">
      <span class="sr-only">Cargando activos...</span>
      <div v-for="n in 4" :key="`asset-skel-${n}`" class="asset-card">
        <div class="asset-head">
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line lg" style="width: 58%"></div>
            <div class="skeleton skeleton-line" style="width: 42%"></div>
          </div>
          <div class="skeleton skeleton-line" style="width: 72px"></div>
        </div>
        <div class="asset-body">
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 52%"></div>
            <div class="skeleton skeleton-line lg" style="width: 78%"></div>
          </div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 48%"></div>
            <div class="skeleton skeleton-line lg" style="width: 70%"></div>
          </div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line" style="width: 44%"></div>
            <div class="skeleton skeleton-line lg" style="width: 66%"></div>
          </div>
        </div>
        <div class="skeleton skeleton-line" style="width: 70%"></div>
        <div class="asset-actions">
          <div class="skeleton skeleton-line" style="width: 86px; height: 28px"></div>
          <div class="skeleton skeleton-line" style="width: 86px; height: 28px"></div>
        </div>
      </div>
    </section>
    <section v-if="error" class="state error">{{ error }}</section>

    <div v-if="!loading" class="assets-grid">
      <div v-for="asset in assets" :key="asset.id" class="asset-card">
        <div class="asset-head">
          <div>
            <h3>{{ asset.name }}</h3>
            <p class="muted">
              {{ asset.platform }} · {{ asset.symbol || "—" }}
            </p>
          </div>
          <span class="chip">{{ typeLabel(asset.assetType) }}</span>
        </div>
        <div class="asset-body">
          <div>
            <p class="muted">Cantidad</p>
            <strong>{{ formatAmount(asset.amount) }}</strong>
          </div>
          <div>
            <p class="muted">Precio EUR</p>
            <strong>{{ asset.currentPriceEur ? formatEur(asset.currentPriceEur) : "—" }}</strong>
          </div>
          <div>
            <p class="muted">Valor EUR</p>
            <strong>{{ formatEur(assetValue(asset)) }}</strong>
          </div>
        </div>
        <p v-if="asset.notes" class="notes">{{ asset.notes }}</p>
        <div class="asset-actions">
          <button class="ghost" @click="openModal(asset)">Editar</button>
          <button class="ghost danger" @click="removeAsset(asset)">Eliminar</button>
        </div>
      </div>
      <div v-if="!assets.length" class="empty-card">
        <p>No hay activos externos todavía.</p>
        <button class="ghost" @click="openModal()">Crear el primero</button>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal">
        <header>
          <h3>{{ form.id ? "Editar activo" : "Nuevo activo" }}</h3>
          <button class="icon danger" @click="closeModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Plataforma
            <input v-model="form.platform" type="text" placeholder="Trade Republic, Mintos..." />
          </label>
          <label>
            Nombre
            <input v-model="form.name" type="text" placeholder="Nombre del activo" />
          </label>
          <label>
            Símbolo
            <input v-model="form.symbol" type="text" placeholder="AAPL, BTC..." />
          </label>
          <label>
            Tipo
            <select v-model="form.assetType">
              <option v-for="type in assetTypes" :key="type" :value="type">
                {{ typeLabel(type) }}
              </option>
            </select>
          </label>
          <label>
            Cantidad
            <input v-model.number="form.amount" type="number" step="0.00000001" />
          </label>
          <div class="row">
            <label>
              Precio EUR (por unidad)
              <input v-model.number="form.currentPriceEur" type="number" step="0.00000001" />
            </label>
            <label>
              Valor EUR total
              <input v-model.number="form.valueEur" type="number" step="0.00000001" />
            </label>
          </div>
          <label>
            Notas
            <textarea v-model="form.notes" rows="3" placeholder="Detalles opcionales"></textarea>
          </label>
        </div>
        <footer>
          <button class="ghost" @click="closeModal">Cancelar</button>
          <button class="primary" @click="saveAsset">Guardar</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchExternalAssets,
  createExternalAsset,
  updateExternalAsset,
  deleteExternalAsset,
} from "../services/externalAssets";

export default {
  name: "ExternalAssetsView",
  data() {
    return {
      assets: [],
      loading: false,
      error: "",
      showModal: false,
      form: this.emptyForm(),
      assetTypes: [
        "STOCK",
        "ETF",
        "CASH",
        "MICROLENDING",
        "CRYPTO",
        "METAL",
        "INDEX",
        "BOND",
        "OTHER",
      ],
    };
  },
  async mounted() {
    await this.loadAssets();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        platform: "",
        name: "",
        symbol: "",
        assetType: "STOCK",
        amount: 0,
        currentPriceEur: null,
        valueEur: null,
        notes: "",
      };
    },
    async loadAssets() {
      this.loading = true;
      this.error = "";
      try {
        const data = await fetchExternalAssets();
        this.assets = (data || []).map((asset) => this.normalizeAsset(asset));
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar los activos.";
      } finally {
        this.loading = false;
      }
    },
    openModal(asset) {
      if (asset) {
        this.form = { ...asset };
      } else {
        this.form = this.emptyForm();
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveAsset() {
      if (!this.form.platform.trim() || !this.form.name.trim()) {
        this.error = "Plataforma y nombre son obligatorios.";
        return;
      }
      try {
        if (this.form.id) {
          const updated = await updateExternalAsset(this.form.id, this.payload());
          const normalized = this.normalizeAsset(updated);
          this.assets = this.assets.map((item) => (item.id === normalized.id ? normalized : item));
        } else {
          const created = await createExternalAsset(this.payload());
          this.assets = [this.normalizeAsset(created), ...this.assets];
        }
        this.closeModal();
      } catch (err) {
        this.error = err?.message || "No se pudo guardar el activo.";
      }
    },
    payload() {
      return {
        platform: this.form.platform,
        name: this.form.name,
        symbol: this.form.symbol || null,
        asset_type: this.form.assetType,
        amount: this.form.amount || 0,
        current_price_eur: this.form.currentPriceEur || null,
        value_eur: this.form.valueEur || null,
        notes: this.form.notes || null,
      };
    },
    async removeAsset(asset) {
      if (!confirm(`¿Eliminar ${asset.name}?`)) return;
      try {
        await deleteExternalAsset(asset.id);
        this.assets = this.assets.filter((item) => item.id !== asset.id);
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar.";
      }
    },
    assetValue(asset) {
      if (asset.valueEur != null) return asset.valueEur;
      if (asset.currentPriceEur != null) return asset.currentPriceEur * asset.amount;
      return 0;
    },
    formatEur(value) {
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    },
    formatAmount(value) {
      return new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 8,
      }).format(Number(value || 0));
    },
    typeLabel(type) {
      const map = {
        STOCK: "Acciones",
        ETF: "ETF",
        CASH: "Dinero",
        MICROLENDING: "Micro‑lending",
        CRYPTO: "Cripto",
        METAL: "Metales",
        INDEX: "Índice",
        BOND: "Bonos",
        OTHER: "Otros",
      };
      return map[type] || type;
    },
    normalizeAsset(asset) {
      return {
        id: asset.id,
        platform: asset.platform,
        name: asset.name,
        symbol: asset.symbol,
        assetType: asset.asset_type || asset.assetType,
        amount: Number(asset.amount || 0),
        currency: asset.currency || "EUR",
        currentPriceEur: asset.current_price_eur ?? asset.currentPriceEur ?? null,
        valueEur: asset.value_eur ?? asset.valueEur ?? null,
        notes: asset.notes,
        created: asset.created,
        updated: asset.updated,
      };
    },
  },
};
</script>

<style scoped>
.external-assets {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
}

.asset-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  display: grid;
  gap: 12px;
  box-shadow: var(--shadow);
}

.asset-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.asset-head h3 {
  margin: 0;
}

.chip {
  background: rgba(56, 189, 248, 0.2);
  color: var(--accent);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.asset-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}

.notes {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.asset-actions {
  display: flex;
  gap: 10px;
}

.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
}

.ghost.danger {
  color: var(--danger);
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.empty-card {
  background: var(--panel-2);
  border: 1px dashed var(--border);
  border-radius: 18px;
  padding: 24px;
  text-align: center;
  color: var(--muted);
  display: grid;
  gap: 12px;
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(540px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.modal-body input[type="file"] {
  display: block;
  min-width: 0;
}

.row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.icon {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
}

.icon.danger {
  color: var(--danger);
}
</style>
